import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Switch } from '@headlessui/react'

import classNames from '@/utils/classNamesLocal'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import TipTapProject from '@/shared/TipTapProject'
import PrimaryButton from '@/shared/Buttons/Primary'
import Card from '@/shared/Card'

import ProfileGeneralAvatar from '@/pages/Users/ProfileGeneralAvatar'
import ProfileGeneralCover from '@/pages/Users/ProfileGeneralCover'

const HEADLINE_MAX = 200
const plain = (html) => (html || '').replace(/<[^>]*>/g, '').trim()

// Name, headline, location, about, availability, pictures. The single form a
// producer's eye lands on first.
const ProfileGeneralBasic = () => {
  const [user, setUser] = useGlobalState('userContainerUser')
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const tipTapIdx = useRef(1)
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const [available, setAvailable] = useState(!!user.isAvailableWork)
  const [savingAvailable, setSavingAvailable] = useState(false)
  const { formState, reset, register, unregister, handleSubmit, setValue, getValues, setError, watch } = useForm({
    defaultValues: {
      display_name: user.displayName || '',
      headline: plain(user.headline),
      location: user.location || '',
      description: user.description || ''
    }
  })
  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    register('description')
    return () => { unregister('description') }
  }, [register])

  const onSubmit = (data) => {
    setLoading(true)
    putpostRequest(`/api/v3/users/${user.id}`, 'PATCH', { user: data }, (err, jsonData) => {
      setLoading(false)
      if (err) {
        if (typeof err !== 'string') { Object.entries(err).forEach(([key, value]) => { setError(key, { type: 'server', message: Array.isArray(value) ? value[0] : value }) }) }
        return
      }
      setUser(jsonData.user)
      if (data.display_name !== currentUser.displayName) { setCurrentUser({ ...currentUser, displayName: data.display_name }) }
      reset({ ...getValues() })
      setToast(randomToastSuccess())
    })
  }

  // Availability saves on its own: it is the one flag people flip often.
  const toggleAvailable = () => {
    const next = !available
    setAvailable(next)
    setSavingAvailable(true)
    putpostRequest(`/api/v3/profiles/${user.profileId}`, 'PATCH', { profile: { is_available_work: next } }, (err) => {
      setSavingAvailable(false)
      if (err) { setAvailable(!next); return }
      setUser({ ...user, isAvailableWork: next })
    })
  }

  const headline = watch('headline') || ''
  const description = getValues().description

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>About you</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>The top of your public profile. Name, one line about you, and whether you're taking work.</p>
    </div>
    <Card footer={<div className='flex justify-end'><PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} disabled={!isDirty} isDirty={isDirty} text='Save' /></div>}>
      <form className='space-y-6'>
        <div className='flex flex-col gap-6 sm:flex-row'>
          <div className='flex-shrink-0'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Picture</label>
            <ProfileGeneralAvatar />
          </div>
          <div className='min-w-0 flex-1 space-y-4'>
            <div>
              <label htmlFor='display_name' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Display name <span className='ml-1 text-xs font-normal text-cccorange'>required</span></label>
              <input id='display_name' type='text' className={errors.display_name ? 'errors mt-1' : 'mt-1'} {...register('display_name', { required: true })} placeholder='Buford' />
              { errors.display_name && <div className='mt-1 text-sm text-red-600'>{errors.display_name.message || 'You need a name. Any name.'}</div> }
            </div>
            <div>
              <label htmlFor='location' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Location</label>
              <input id='location' type='text' className={errors.location ? 'errors mt-1' : 'mt-1'} {...register('location')} placeholder='San Francisco' />
              { errors.location && <div className='mt-1 text-sm text-red-600'>{errors.location.message}</div> }
            </div>
            <div>
              <div className='flex items-center justify-between'>
                <label htmlFor='headline' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Headline</label>
                <span className={classNames('text-xs', headline.length > HEADLINE_MAX ? 'text-red-600' : 'text-gray-400')}>{headline.length}/{HEADLINE_MAX}</span>
              </div>
              <input id='headline' type='text' maxLength={HEADLINE_MAX} className={errors.headline ? 'errors mt-1' : 'mt-1'} {...register('headline')} placeholder='Warm, character-driven voice actor. Home studio, quick turnaround.' />
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>One line under your name.</p>
              { errors.headline && <div className='mt-1 text-sm text-red-600'>{errors.headline.message}</div> }
            </div>
          </div>
        </div>

        <Switch.Group as='div' className='flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-900'>
          <Switch.Label as='span' className='flex flex-col'>
            <span className='text-sm font-medium text-gray-900 dark:text-gray-200'>Available for work</span>
            <span className='text-xs text-gray-500 dark:text-gray-400'>Shows a badge on your profile and in talent search.</span>
          </Switch.Label>
          <Switch checked={available} onChange={toggleAvailable} disabled={savingAvailable}
            className={classNames(available ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cccpurple focus:ring-offset-2 disabled:opacity-60')}>
            <span aria-hidden='true' className={classNames(available ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition')} />
          </Switch>
        </Switch.Group>

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>About</label>
          <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>
            Who you are, what you've done, what you're into.
            { currentUser.canUploadImages ? ' Drag or paste images straight in.' : <> Images are a <a href='/subscriptions' target='_blank' rel='noreferrer' className='underline'>Premium</a> perk.</> }
          </p>
          <TipTapProject html={description} idx={tipTapIdx.current} imageUploadSupported={currentUser.canUploadImages}
            updateHtml={(val) => setValue('description', val, { shouldDirty: true, shouldValidate: true })}
            placeholder="I've been voicing cartoon villains since I was twelve. Home studio, same-day turnaround, and I actually read the script." />
        </div>

        <ProfileGeneralCover />
      </form>
    </Card>
  </div>
}

export default ProfileGeneralBasic
